import React from "react";

import { Alignment, Button, Classes, ControlGroup, Dialog, FormGroup, HTMLSelect, InputGroup, IOptionProps, Menu, MenuItem, NonIdealState, Popover } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { FlexJustify } from "common/fragments/Flex";
import { Intent } from "core/components/IntentContext";
import { DictionaryEntryFormPanel } from "core/components/label/DictionaryEntryForm";
import { LabelRegistry } from "core/components/label/ILabel";
import { ILabelDefinition } from "core/components/label/LabelProvider";
import XHRResolverService from "core/service/resolver/impl/XHRResolver.service";
import IResolverService from "core/service/resolver/IResolverService";
import ServiceRegistry from "core/util/ServiceRegistry";
import { Entity, Field } from "typegraph";
import styled from "styled-components";

// tslint:disable:max-classes-per-file

const Cont = styled.div`
	display: grid;
	grid-template:
    " top top " auto
    " left right " 1fr
    " bottom bottom " auto
    / 1fr 1fr;
    grid-gap: 15px;
`;


interface ILabelDownloadState {
	loading: boolean;
}

class LabelDownload extends React.Component<any, ILabelDownloadState> {

	public state = { loading: false };

	public render() {

		return (
			<Button
				loading={this.state.loading}
				intent="primary"
				icon={IconNames.DOWNLOAD}
				text="CSV"
				onClick={this.download}
			/>
		);
	}

	private download = () => {
		const registered = LabelRegistry.getAll();

		this.setState({loading: true}, () => {
			ServiceRegistry.get(XHRResolverService).execute(`query { dictionaryEntry_many(params: {}) { key language value } }`).then((res) => {
				this.setState({loading: false}, () => {

					const overrides: Array<{ key: string; language: string; value: string; }> = res && JSON.parse(res).data.dictionaryEntries || [];

					const languages: string[] = [];

					const overrideMap = overrides.reduce((a, c) => {

						if (!a[c.key]) {
							a[c.key] = {};
						}

						if (!a[c.key][c.language]) {
							a[c.key][c.language] = c;
						}

						if (languages.indexOf(c.language) < 0) {
							languages.push(c.language);
						}

						return a;
					}, {});

					const columns = ['key', 'defaultValue', 'variables', ...languages];

					const arr: string[][] = [
						columns,
						...registered.map<string[]>((labelDef) => columns.map((col) => {
							switch(col) {
								case "key":
									return `"${labelDef.k}"`;
								case "defaultValue":
									return `"${labelDef.v}"`;
								case "variables":
									return labelDef.argNames ? `"${labelDef.argNames.join(',')}"` : "";
								default:
									return overrideMap[labelDef.k] && overrideMap[labelDef.k][col] && `"${overrideMap[labelDef.k][col].value}"` || "";
							}
						}))
					];

					console.log(arr);

					const csv = encodeURI('data:text/csv;charset=utf-8;\n' + arr.map((row) => row.join(',')).join('\n'));

					console.log(csv);

					window.open(csv);
				});
			});
		});
	}
}

interface ILabelListProps {
	useDialog?: boolean;
}

interface ILabelListState {
	data: ILabelDefinition[];
	isOpen: boolean;
	selected: null | ILabelDefinition;
	query: string;
}

export default class LabelList extends React.Component<ILabelListProps, ILabelListState> {

	public state: ILabelListState = {
		data: [],
		isOpen: false,
		selected: null,
		query: ""
	};

	public render() {
		const { useDialog = true } = this.props;

		if (useDialog) {
			return [
				<Button key="btn" icon={IconNames.EDIT} minimal={true} onClick={() => this.setState({isOpen: !this.state.isOpen})} />,
				<Dialog
					key="dialog"
					className={Classes.DARK}
					isOpen={this.state.isOpen}
					title="Edit All Labels"
					style={{width: '100%', maxWidth: '800px'}}
					onClose={() => this.setState({isOpen: !this.state.isOpen})}
				>
					{this.renderList()}
				</Dialog>
			];
		}

		return (
			<div>
				{this.renderList()}
			</div>
		);
	}

	public componentDidMount() {
		this.load();
	}

	private renderList = () => {
		const { data, selected, query } = this.state;

		const results = !!query ? data.filter((ld) => (ld.k + ' ' + ld.v).toLowerCase().indexOf(query.toLowerCase()) > -1) : data;

		const sLabel = selected ? results.find((l) => l.k === selected.k) : null;

		return (
			<Cont>
				<FormGroup style={{gridArea: "top"}} label="Filter" helperText={data.length + ' Labels Available'}>
					<ControlGroup>
						<InputGroup
							placeholder="Search"
							onChange={(e: any) => this.setState({query: e.target.value})}
							value={this.state.query}
							rightElement={
								<Button
									icon={IconNames.SMALL_CROSS}
									minimal={true}
									small={true}
									disabled={!query}
									onClick={() => this.setState({query: ""})}
								/>
							}
						/>
					</ControlGroup>
				</FormGroup>
				<FormGroup style={{gridArea: "left"}} label={!!query ? `${results.length} Matches` : "All Labels"}>
					<Menu style={{width: '100%', height: '500px', overflowY: 'scroll'}}>
						{
							results.map((labelDef) => (
								<MenuItem
									key={labelDef.k}
									text={
										<div>
											<div>{labelDef.k}</div>
											<small className={Classes.TEXT_MUTED}>{labelDef.v}</small>
										</div>
									}
									active={!!selected && selected.k === labelDef.k}
									onClick={this.selectItem(labelDef)}
								/>
							))
						}
					</Menu>
				</FormGroup>
				<FormGroup style={{gridArea: "bottom"}} label="Import/Export">
						<LabelDownload />
				</FormGroup>
				<div style={{gridArea: "right"}}>
					{
						selected ? (
							<div>
								<DictionaryEntryFormPanel {...selected} />
							</div>
						) : (
							<NonIdealState title="Select a label" description="All registed labels will show in list to the left" />
						)
					}
				</div>
			</Cont>
		);
	}

	private selectItem = (labelDef: ILabelDefinition) => () => this.setState({selected: this.state.selected && this.state.selected.k === labelDef.k ? null : labelDef });

	private load = () => {
		this.setState({data: LabelRegistry.getAll()});
	}

	// for now guess will just do this
	private downloadList = () => {
		window.open(
			encodeURI(
				'data:text/csv;charset=utf-8;\n' +
				+ 'key,defaultValue\n'
				+ this.state.data.map(
					(labelDef) => `"${labelDef.k}","${labelDef.v}"`
				).join('\n')
			)
		);
	}
}



// WEBPACK FOOTER //
// ./src/core/components/editor/label/LabelList.tsx